import { Injectable } from '@angular/core';
import { InMemoryCacheService, UtilsService } from 'src/app/common';
import { OpportunitiesApiService } from '../../opportunities/opportunities-api.service';
import { OpportunitiesWorkspaceResponse } from '../../opportunities/models/opportunity.models';
import { GoogleAnalyticsKpiService } from '../google-analytics-kpi/google-analytics-kpi.service';
import { DomainResearchService } from '../domain-research/domain-research.service';
import { PageRequest } from '../models/on-page';
import { RankedKeywordsRequest } from '../models/keyword-search-request';
import { BacklinksHistoryRequest, BacklinksRequest, HistoricalRankOverviewRequest } from '../models/site-search-request';
import { DateRangeOptions } from '../models/date-range-options';
import { OnPageService } from '../on-page/on-page.service';
import { SeoApiService } from '../seo-api.service';
import {
  SeoPdfCompetitorExample,
  SeoPdfDashboardSnapshot,
  SeoPdfGaSnapshot,
  SeoPdfKeywordExample,
  SeoPdfProjectInfo,
  SeoPdfSourceData,
  SeoPdfTechnicalIssue,
} from './seo-comprehensive-pdf.models';

interface HistoricalRankOverviewItem {
  etv?: number | null;
}

interface RankedKeywordItem {
  keyword?: string | null;
  rank_group?: number | null;
  rankGroup?: number | null;
  rankedSerpElement?: {
    serpItem?: {
      rankGroup?: number | null;
    } | null;
  } | null;
  search_intent_info?: {
    main_intent?: string | null;
  } | null;
  searchIntentInfo?: {
    mainIntent?: string | null;
  } | null;
  searchIntent?: string | null;
  keyword_info?: {
    search_volume?: number | null;
    competition_level?: string | null;
    keyword_difficulty?: number | null;
  } | null;
  keywordInfo?: {
    searchVolume?: number | null;
    competitionLevel?: string | null;
    competition?: number | null;
    keywordDifficulty?: number | null;
  } | null;
  keywordData?: {
    keywordInfo?: {
      searchVolume?: number | null;
      competitionLevel?: string | null;
      competition?: number | null;
    } | null;
    keywordProperties?: {
      keywordDifficulty?: number | null;
    } | null;
  } | null;
  keyword_properties?: {
    keyword_difficulty?: number | null;
  } | null;
}

interface BacklinksSummaryResponse {
  rank?: number | null;
  backlinks?: number | null;
  referringDomains?: number | null;
}

interface BacklinksHistoryItem {
  brokenBacklinks?: number | null;
}

interface GoogleAnalyticsMetricValue {
  value?: string;
}

interface GoogleAnalyticsRow {
  dimensionValues?: GoogleAnalyticsMetricValue[];
  metricValues?: GoogleAnalyticsMetricValue[];
}

interface GoogleAnalyticsResponse {
  rows?: GoogleAnalyticsRow[];
}

interface DomainResearchCompetitor {
  name?: string | null;
  domain?: string | null;
}

interface DomainResearchCompetitorRankedKeyword {
  domain?: string | null;
  top10Keywords?: Array<{
    keyword?: string | null;
    rankGroup?: number | null;
    competitorSerp?: {
      rankAbsolute?: number | null;
      domain?: string | null;
      title?: string | null;
    } | null;
    targetSerp?: {
      rankAbsolute?: number | null;
      domain?: string | null;
      title?: string | null;
    } | null;
  }>;
  top11to20Keywords?: Array<{
    keyword?: string | null;
    rankGroup?: number | null;
    competitorSerp?: {
      rankAbsolute?: number | null;
      domain?: string | null;
      title?: string | null;
    } | null;
    targetSerp?: {
      rankAbsolute?: number | null;
      domain?: string | null;
      title?: string | null;
    } | null;
  }>;
}

interface SiteAuditScanRecord {
  id?: number;
  taskId?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoComprehensivePdfSourceService {
  public constructor(
    private readonly cache: InMemoryCacheService,
    private readonly utils: UtilsService,
    private readonly seoApiService: SeoApiService,
    private readonly googleAnalyticsKpiService: GoogleAnalyticsKpiService,
    private readonly opportunitiesApiService: OpportunitiesApiService,
    private readonly onPageService: OnPageService,
    private readonly domainResearchService: DomainResearchService
  ) {}

  public async buildSourceData(): Promise<SeoPdfSourceData | null> {
    const project = this.buildProjectInfo();
    if (!project) {
      return null;
    }

    const [
      dashboard,
      googleAnalytics,
      opportunities,
      technicalHealth,
      keywordExamples,
      competitorExamples,
    ] = await Promise.all([
      this.loadDashboardSnapshot(),
      this.loadGaSnapshot(),
      this.loadOpportunities(),
      this.loadTechnicalHealth(),
      this.loadKeywordExamples(),
      this.loadCompetitorExamples(),
    ]);

    return {
      project,
      dashboard,
      googleAnalytics,
      opportunities,
      technicalHealth,
      keywordExamples,
      competitorExamples,
    };
  }

  public buildProjectInfo(): SeoPdfProjectInfo | null {
    const currentProject = this.cache.currentProject;
    if (!currentProject?.id) {
      return null;
    }

    return {
      projectId: currentProject.id,
      projectName: currentProject.name ?? currentProject.domain ?? 'SEO Project',
      domain: currentProject.domain ?? '',
      generatedAt: new Date().toISOString(),
    };
  }

  public mapKeywordExample(item: RankedKeywordItem): SeoPdfKeywordExample {
    const rank = this.normalizeNumber(
      item.rank_group ??
      item.rankGroup ??
      item.rankedSerpElement?.serpItem?.rankGroup
    );
    const intent = item.search_intent_info?.main_intent ?? item.searchIntentInfo?.mainIntent ?? item.searchIntent ?? null;
    const difficulty = this.normalizeKeywordDifficulty(item);

    return {
      keyword: item.keyword ?? 'Unknown keyword',
      rank,
      intent,
      volume: this.normalizeNumber(
        item.keyword_info?.search_volume ??
        item.keywordInfo?.searchVolume ??
        item.keywordData?.keywordInfo?.searchVolume
      ),
      difficulty,
      whyItMatters: this.buildKeywordWhyItMatters(rank, intent, difficulty),
    };
  }

  public mapTechnicalIssues(siteLevelIssues: unknown[], pageLevelIssues: unknown[]): SeoPdfTechnicalIssue[] {
    const siteIssues = Array.isArray(siteLevelIssues)
      ? siteLevelIssues.map((issue: unknown) => this.mapSiteLevelIssue(issue))
      : [];
    const pageIssues = Array.isArray(pageLevelIssues)
      ? pageLevelIssues.map((issue: unknown) => this.mapPageLevelIssue(issue))
      : [];

    return [...siteIssues, ...pageIssues].filter((issue: SeoPdfTechnicalIssue | null): issue is SeoPdfTechnicalIssue => !!issue);
  }

  private async loadDashboardSnapshot(): Promise<SeoPdfDashboardSnapshot | null> {
    const domain = this.utils.stripHost(this.cache.currentProject?.domain);
    if (!domain) {
      return null;
    }

    const dateRange = this.utils.getStableHistoricalRange(DateRangeOptions.Last12Months);
    const historicalRankOverviewRequest: HistoricalRankOverviewRequest = {
      target: domain,
      locationCode: this.cache?.selectedProject?.locationCode ? this.cache.selectedProject.locationCode : 2840,
      languageCode: this.cache?.selectedProject?.languageCode ? this.cache.selectedProject.languageCode : 'en',
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    };
    const rankedKeywordsRequest: RankedKeywordsRequest = {
      target: domain,
      locationCode: this.cache?.selectedProject?.locationCode ? this.cache.selectedProject.locationCode : 2840,
      languageCode: this.cache?.selectedProject?.languageCode ? this.cache.selectedProject.languageCode : 'en',
      limit: this.cache?.seoLimits?.dashboardRankedKeywords,
      filtersString: '[[["ranked_serp_element.serp_item.rank_group","<=",10],"and",["ranked_serp_element.serp_item.type","=","organic"]]]',
    };
    const backlinksRequest: BacklinksRequest = {
      target: domain,
      internalListLimit: this.cache?.seoLimits?.dashboardBacklinksInternal,
      includeSubdomains: true,
      backlinksStatusType: 'all',
    };
    const backlinksHistoryRequest: BacklinksHistoryRequest = {
      target: this.utils.stripHostAndWWW(this.cache.currentProject?.domain),
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    };

    const [historicalResponse, rankedResponse, backlinksSummaryResponse, backlinksHistoryResponse] = await Promise.all([
      this.seoApiService.getHistoricalRankOverview(historicalRankOverviewRequest, false),
      this.seoApiService.getRankedKeywords(rankedKeywordsRequest, false),
      this.seoApiService.getBacklinksSummary(backlinksRequest, false),
      this.seoApiService.getBacklinksHistory(backlinksHistoryRequest, false),
    ]);

    const historicalItems = this.readItems<HistoricalRankOverviewItem>(historicalResponse.data);
    const latestHistoricalItem = historicalItems.length > 0 ? historicalItems[historicalItems.length - 1] : null;
    const rankedItems = this.readItems<RankedKeywordItem>(rankedResponse.data);
    const backlinksSummary = (backlinksSummaryResponse.data ?? null) as BacklinksSummaryResponse | null;
    const backlinksHistoryItems = this.readItems<BacklinksHistoryItem>(backlinksHistoryResponse.data);

    return {
      domainRank: this.normalizeNumber(backlinksSummary?.rank),
      organicTraffic: this.normalizeNumber(latestHistoricalItem?.etv),
      backlinks: this.normalizeNumber(backlinksSummary?.backlinks),
      referringDomains: this.normalizeNumber(backlinksSummary?.referringDomains),
      brokenBacklinks: backlinksHistoryItems.reduce((sum: number, item: BacklinksHistoryItem) => sum + (this.normalizeNumber(item.brokenBacklinks) ?? 0), 0),
      rankedKeywords: rankedItems.length,
    };
  }

  private async loadGaSnapshot(): Promise<SeoPdfGaSnapshot | null> {
    const propertyId = this.cache?.selectedProject?.externalProviders?.google?.propertyId;
    if (!propertyId) {
      return null;
    }

    const query = {
      accessToken: this.cache?.selectedProject?.externalProviders?.google?.accessToken,
      userId: this.cache.userId,
      propertyId,
      projectId: this.cache?.selectedProject?.id,
      reportData: {
        metrics: [
          { name: 'sessions' },
          { name: 'averageSessionDuration' },
          { name: 'bounceRate' },
          { name: 'screenPageViews' },
          { name: 'activeUsers' },
          { name: 'newUsers' },
        ],
        dateRanges: [{ startDate: '30daysAgo', endDate: 'today' }],
      },
    };

    const [generalResponse, visitsTrendResponse, bounceTrendResponse, sourceMixResponse, visitorMixResponse] = await Promise.all([
      this.googleAnalyticsKpiService.getAnalyticsQuery(query as never),
      this.googleAnalyticsKpiService.getAnalyticsQuery(this.buildAnalyticsQuery({
        dimensions: [{ name: 'week' }],
        metrics: [{ name: 'sessions' }],
        dateRanges: [{ startDate: '90daysAgo', endDate: 'today' }],
      }) as never),
      this.googleAnalyticsKpiService.getAnalyticsQuery(this.buildAnalyticsQuery({
        dimensions: [{ name: 'week' }],
        metrics: [{ name: 'bounceRate' }],
        dateRanges: [{ startDate: '90daysAgo', endDate: 'today' }],
      }) as never),
      this.googleAnalyticsKpiService.getAnalyticsQuery(this.buildAnalyticsQuery({
        dimensions: [{ name: 'sessionSource' }],
        metrics: [{ name: 'sessions' }],
        dateRanges: [{ startDate: '30daysAgo', endDate: 'today' }],
      }) as never),
      this.googleAnalyticsKpiService.getAnalyticsQuery(this.buildAnalyticsQuery({
        dimensions: [{ name: 'newVsReturning' }],
        metrics: [{ name: 'users' }],
        dateRanges: [{ startDate: '30daysAgo', endDate: 'today' }],
      }) as never),
    ]);

    const analyticsData = generalResponse as GoogleAnalyticsResponse | null;
    const firstRow = analyticsData?.rows?.[0];
    const metricValues = firstRow?.metricValues ?? [];

    if (metricValues.length === 0) {
      return null;
    }

    return {
      visits: this.readMetricValue(metricValues, 0),
      totalSessions: this.readMetricValue(metricValues, 0),
      avgSessionDuration: this.readMetricValue(metricValues, 1),
      bounceRate: this.readMetricValue(metricValues, 2, true),
      pageViews: this.readMetricValue(metricValues, 3),
      totalActiveUsers: this.readMetricValue(metricValues, 4),
      totalNewUsers: this.readMetricValue(metricValues, 5),
      visitTrend: this.mapAnalyticsSeries(visitsTrendResponse as GoogleAnalyticsResponse | null, 'value'),
      bounceRateTrend: this.mapAnalyticsSeries(bounceTrendResponse as GoogleAnalyticsResponse | null, 'percent'),
      trafficSources: this.mapTrafficSources(sourceMixResponse as GoogleAnalyticsResponse | null),
      visitorMix: this.mapVisitorMix(visitorMixResponse as GoogleAnalyticsResponse | null),
    };
  }

  private async loadOpportunities(): Promise<OpportunitiesWorkspaceResponse | null> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return null;
    }

    const { data } = await this.opportunitiesApiService.getOpportunities(projectId);
    return data;
  }

  private async loadTechnicalHealth(): Promise<SeoPdfSourceData['technicalHealth']> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return null;
    }

    const latestScanResponse = await this.onPageService.getLatestScan(projectId);
    const scanRecords = Array.isArray(latestScanResponse?.data) ? latestScanResponse.data as SiteAuditScanRecord[] : [];
    const completedScan = scanRecords.find((record: SiteAuditScanRecord) => record.status === 'Completed');

    if (!completedScan?.id || !completedScan.taskId) {
      return null;
    }

    await this.onPageService.getSummary(completedScan.id);
    const pageRequest: PageRequest = {
      id: completedScan.taskId,
    };
    await this.onPageService.getPages(pageRequest, completedScan.id);

    return {
      siteHealthScore: this.normalizeNumber(this.onPageService.summaryInfo?.score),
      crawledPages: this.normalizeNumber(this.onPageService.summaryInfo?.pagesCrawled),
      lastCrawlAt: this.onPageService.summaryInfo?.lastCrawlTime ?? null,
      issues: this.mapTechnicalIssues(this.onPageService.siteLevelIssues, this.onPageService.pageLevelIssues),
    };
  }

  private async loadKeywordExamples(): Promise<SeoPdfKeywordExample[]> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return [];
    }

    const response = await this.domainResearchService.getDomainResearchData(projectId, 'keyword');
    const rankedKeywords = this.readItemsFromPaths<RankedKeywordItem>(response.data, [
      ['rankedKeywords', 'rankedKeywords', 'keywords'],
      ['rankedKeywords', 'rankedKeywords', 'top10Keywords'],
      ['rankedKeywords', 'rankedKeywords', 'top11to20Keywords'],
      ['rankedKeywords', 'keywords'],
      ['rankedKeywords', 'top10Keywords'],
      ['rankedKeywords', 'top11to20Keywords'],
      ['0', 'keywordProjectInfo', '0', 'rankedKeywords', 'keywords'],
      ['0', 'keywordProjectInfo', '0', 'rankedKeywords', 'top10Keywords'],
      ['0', 'keywordProjectInfo', '0', 'rankedKeywords', 'top11to20Keywords'],
    ]);

    return rankedKeywords
      .filter((item: RankedKeywordItem) => !!item.keyword)
      .slice(0, 6)
      .map((item: RankedKeywordItem) => this.mapKeywordExample(item));
  }

  private async loadCompetitorExamples(): Promise<SeoPdfCompetitorExample[]> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return [];
    }

    const response = await this.domainResearchService.getDomainResearchData(projectId, 'keyword');
    const keywordPayload = response.data;
    const competitors = this.readItemsFromPaths<DomainResearchCompetitor>(keywordPayload, [
      ['keywordIntersect', 'competitorIntersect', 'competitors'],
      ['0', 'keywordProjectInfo', '0', 'competitorIntersect', 'competitors'],
    ]);
    const competitorRankedKeywords = this.readItemsFromPaths<DomainResearchCompetitorRankedKeyword>(keywordPayload, [
      ['0', 'keywordProjectInfo', '0', 'competitorRankedKeywords'],
      ['keywordIntersect', 'competitorRankedKeywords'],
    ]);

    const examples: SeoPdfCompetitorExample[] = [];

    competitors.forEach((competitor: DomainResearchCompetitor) => {
      const matchedKeywords = competitorRankedKeywords.find((item: DomainResearchCompetitorRankedKeyword) => item.domain === competitor.domain);
      const nearPageOneKeyword = matchedKeywords?.top11to20Keywords?.[0] ?? matchedKeywords?.top10Keywords?.[0];
      if (!nearPageOneKeyword?.keyword || !competitor.domain) {
        return;
      }

      const competitorRank = this.normalizeNumber(nearPageOneKeyword.competitorSerp?.rankAbsolute);
      const targetRank = this.normalizeNumber(nearPageOneKeyword.targetSerp?.rankAbsolute) ?? this.normalizeNumber(nearPageOneKeyword.rankGroup);

      examples.push({
        topic: nearPageOneKeyword.keyword,
        competitor: competitor.domain,
        targetState: targetRank !== null ? `Ranking around position ${targetRank}` : 'Target ranking is not clearly surfaced',
        whyCompetitorWins: competitorRank !== null
          ? `Competitor currently appears ahead at roughly position ${competitorRank}.`
          : 'Competitor has stronger visible SERP ownership for this topic.',
        suggestedResponse: 'Improve the target page for this topic and align internal links and content intent more tightly.',
      });
    });

    return examples.slice(0, 3);
  }

  private mapSiteLevelIssue(issue: unknown): SeoPdfTechnicalIssue | null {
    const candidate = issue as {
      name?: string;
      indicator?: string;
      details?: string;
    } | null;

    if (!candidate?.name) {
      return null;
    }

    const isPassed = candidate.indicator === 'FOUND' || candidate.indicator === 'PASSED';
    return {
      theme: candidate.name,
      status: candidate.indicator ?? 'UNKNOWN',
      count: isPassed ? 0 : 1,
      businessImplication: candidate.details ?? 'Site-level configuration may affect crawl trust and discoverability.',
    };
  }

  private mapPageLevelIssue(issue: unknown): SeoPdfTechnicalIssue | null {
    const candidate = issue as {
      title?: string;
      indicator?: string;
      description?: number | string | null;
    } | null;

    if (!candidate?.title) {
      return null;
    }

    const count = this.normalizeNumber(candidate.description) ?? 0;

    return {
      theme: candidate.title,
      status: candidate.indicator ?? 'UNKNOWN',
      count,
      businessImplication: `${candidate.title} can reduce crawl quality, indexation confidence, or page performance.`,
    };
  }

  private buildKeywordWhyItMatters(
    rank: number | null,
    intent: string | null,
    difficulty: number | null
  ): string {
    if (rank !== null && rank >= 11 && rank <= 20) {
      return 'Close to page one and a strong optimization candidate.';
    }

    if (intent && intent.toLowerCase().includes('transaction')) {
      return 'Commercial intent makes this query materially valuable.';
    }

    if (difficulty !== null && difficulty <= 35) {
      return 'Difficulty is manageable enough for a realistic near-term gain.';
    }

    return 'Useful representative evidence for current search visibility.';
  }

  private normalizeKeywordDifficulty(item: RankedKeywordItem): number | null {
    const directDifficulty = this.normalizeNumber(
      item.keyword_info?.keyword_difficulty ??
      item.keywordInfo?.keywordDifficulty ??
      item.keywordData?.keywordProperties?.keywordDifficulty ??
      item.keyword_properties?.keyword_difficulty
    );
    if (directDifficulty !== null) {
      return directDifficulty;
    }

    const competitionLevel =
      item.keyword_info?.competition_level ??
      item.keywordInfo?.competitionLevel ??
      item.keywordData?.keywordInfo?.competitionLevel;
    if (!competitionLevel) {
      const competition = this.normalizeNumber(item.keywordInfo?.competition ?? item.keywordData?.keywordInfo?.competition);
      return competition !== null ? Math.round(competition * 100) : null;
    }

    switch (competitionLevel.toUpperCase()) {
      case 'LOW':
        return 25;
      case 'MEDIUM':
        return 50;
      case 'HIGH':
        return 75;
      default:
        return null;
    }
  }

  private readMetricValue(values: GoogleAnalyticsMetricValue[], index: number, shouldScalePercent: boolean = false): number | null {
    const rawValue = values[index]?.value;
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return null;
    }

    const parsedValue = Number(rawValue);
    if (Number.isNaN(parsedValue)) {
      return null;
    }

    if (shouldScalePercent) {
      return Number((parsedValue * 100).toFixed(2));
    }

    return Number(parsedValue.toFixed(2));
  }

  private readItems<T>(responseData: unknown): T[] {
    if (!responseData || typeof responseData !== 'object') {
      return [];
    }

    const candidate = responseData as { items?: T[] };
    return Array.isArray(candidate.items) ? candidate.items : [];
  }

  private readItemsFromPaths<T>(responseData: unknown, paths: string[][]): T[] {
    for (const path of paths) {
      const resolved = this.resolvePath(responseData, path);
      if (Array.isArray(resolved)) {
        return resolved as T[];
      }
    }

    return [];
  }

  private resolvePath(value: unknown, path: string[]): unknown {
    return path.reduce((current: unknown, segment: string) => {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }

      if (typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }

      return undefined;
    }, value);
  }

  private buildAnalyticsQuery(reportData: {
    dimensions?: Array<{ name: string }>;
    metrics: Array<{ name: string }>;
    dateRanges: Array<{ startDate: string; endDate: string }>;
  }): {
    accessToken: string;
    userId: string;
    propertyId: string;
    projectId: string;
    reportData: {
      dimensions?: Array<{ name: string }>;
      metrics: Array<{ name: string }>;
      dateRanges: Array<{ startDate: string; endDate: string }>;
    };
  } {
    return {
      accessToken: this.cache?.selectedProject?.externalProviders?.google?.accessToken,
      userId: this.cache.userId,
      propertyId: this.cache?.selectedProject?.externalProviders?.google?.propertyId,
      projectId: this.cache?.selectedProject?.id,
      reportData,
    };
  }

  private mapAnalyticsSeries(
    response: GoogleAnalyticsResponse | null,
    metricMode: 'value' | 'percent'
  ): Array<{ label: string; value: number }> {
    const rows = Array.isArray(response?.rows) ? response.rows : [];
    return rows
      .map((row: GoogleAnalyticsRow) => {
        const label = row.dimensionValues?.[0]?.value ?? null;
        const value = this.readMetricValue(row.metricValues ?? [], 0, metricMode === 'percent');
        if (!label || value === null) {
          return null;
        }

        return {
          label,
          value,
        };
      })
      .filter((item: { label: string; value: number } | null): item is { label: string; value: number } => !!item);
  }

  private mapTrafficSources(response: GoogleAnalyticsResponse | null): Array<{ source: string; sessions: number }> {
    const rows = Array.isArray(response?.rows) ? response.rows : [];
    return rows
      .map((row: GoogleAnalyticsRow) => {
        const source = row.dimensionValues?.[0]?.value ?? null;
        const sessions = this.readMetricValue(row.metricValues ?? [], 0);
        if (!source || sessions === null) {
          return null;
        }

        return {
          source,
          sessions,
        };
      })
      .filter((item: { source: string; sessions: number } | null): item is { source: string; sessions: number } => !!item)
      .slice(0, 5);
  }

  private mapVisitorMix(response: GoogleAnalyticsResponse | null): Array<{ type: string; users: number }> {
    const rows = Array.isArray(response?.rows) ? response.rows : [];
    return rows
      .map((row: GoogleAnalyticsRow) => {
        const type = row.dimensionValues?.[0]?.value ?? null;
        const users = this.readMetricValue(row.metricValues ?? [], 0);
        if (!type || users === null) {
          return null;
        }

        return {
          type,
          users,
        };
      })
      .filter((item: { type: string; users: number } | null): item is { type: string; users: number } => !!item);
  }

  private normalizeNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    return null;
  }
}
